import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, Save, Edit3, MapPin, Users, Circle, RectangleHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';

interface TableEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tableData: TableData) => void;
  table: TableData | null;
}

interface TableData {
  id: string;
  name: string;
  seats: number;
  status: 'Available' | 'Occupied' | 'Reserved' | 'Maintenance';
  location: string;
  description: string;
  shape: 'circle' | 'rectangle';
  color: string;
  // Required positioning and sizing fields for backend compatibility
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  rotation: number;
  floor: string;
}

const statusOptions = [
  { value: 'Available', label: 'Available', color: 'bg-green-100 text-green-800' },
  { value: 'Occupied', label: 'Occupied', color: 'bg-red-100 text-red-800' },
  { value: 'Reserved', label: 'Reserved', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'Maintenance', label: 'Maintenance', color: 'bg-orange-100 text-orange-800' }
];



const shapeOptions = [
  { value: 'circle', label: '', icon: <Circle size={16} /> },
  { value: 'rectangle', label: '', icon: <RectangleHorizontal size={16} /> }
];

export default function TableEditDialog({ isOpen, onClose, onSave, table }: TableEditDialogProps) {
  const [formData, setFormData] = useState<TableData>({
    id: '',
    name: '',
    seats: 4,
    status: 'Available',
    location: '',
    description: '',
    shape: 'circle',
    color: '#22c55e', // Default available color
    xposition: 0,
    yposition: 0,
    width: 100,
    height: 100,
    rotation: 0,
    floor: '1' // Default to floor 1
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && table) {
      setFormData({
        ...table,
        description: table.description || '',
        shape: table.shape || 'circle'
      });
    } else if (isOpen && !table) {
      // New table - ID will be auto-generated by backend with sequential naming
      setFormData({
        id: `temp-${Date.now()}`, // Temporary ID, backend will assign sequential name
        name: '',
        seats: 4,
        status: 'Available',
        location: '',
        description: 'circle table', // Auto-generate description from shape
        shape: 'circle',
        color: '#22c55e', // Available status color
        xposition: 50,
        yposition: 50,
        width: 100,
        height: 100,
        rotation: 0,
        floor: '1' // Default to floor 1
      });
    }
    setErrors({});
  }, [isOpen, table]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Table name is required';
    }
    
    if (formData.seats < 1 || formData.seats > 20) {
      newErrors.seats = 'Seats must be between 1 and 20';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      onSave(formData);
      onClose();
    } catch (error) {
      console.error('Failed to save table:', error);
      toast.error('Failed to save table');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof TableData, value: string | number) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate description when shape changes
      if (field === 'shape') {
        updated.description = `${value.toString().toLowerCase()} table`;
      }
      
      // Update color when status changes
      if (field === 'status') {
        const statusValue = value as TableData['status'];
        switch (statusValue) {
          case 'Available':
            updated.color = '#22c55e';
            break;
          case 'Occupied':
            updated.color = '#ef4444';
            break;
          case 'Reserved':
            updated.color = '#f59e0b';
            break;
          default:
            updated.color = '#6b7280';
        }
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50  bg-black bg-opacity-50 !mt-0 flex items-center justify-center p-4">
      <Card className="w-full max-w-md sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Edit3 className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">
                {table ? 'Edit Table' : 'Add New Table'}
              </CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 overflow-y-auto flex-1 px-6 py-4">
          {/* Basic Information */}
          <div className="space-y-2">
            <Label htmlFor="table-name">Table Name *</Label>
            <Input
              id="table-name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Table 1, Corner Table, Window Booth"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>

          {/* Seats, Status, and Floor */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="seats">Seats *</Label>
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-slate-500" />
                <Input
                  id="seats"
                  type="number"
                  min="1"
                  max="20"
                  value={formData.seats}
                  onChange={(e) => handleInputChange('seats', Number.parseInt(e.target.value, 10) || 0)}
                  className={errors.seats ? 'border-red-500' : ''}
                />
              </div>
              {errors.seats && <p className="text-red-500 text-sm">{errors.seats}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="floor">Floor *</Label>
              <select
                id="floor"
                value={formData.floor}
                onChange={(e) => handleInputChange('floor', e.target.value)}
                className="w-full p-2 border rounded-md"
              >
                <option value="1">1st Floor</option>
                <option value="2">2nd Floor</option>
                <option value="3">Rooftop</option>
              </select>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <div className="flex items-center space-x-2">
              <MapPin className="h-4 w-4 text-slate-500" />
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., Main Hall, Window Side, VIP Area"
                className={`flex-1 text-muted ${errors.location ? 'border-red-500' : ''}`}
              />
            </div>
            {errors.location && <p className="text-red-500 text-sm">{errors.location}</p>}
          </div>

          {/* Visual Properties */}
          <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
            <Label className="text-sm font-medium">Table Shape</Label>
            
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                {shapeOptions.map(shape => (
                  <Button
                    key={shape.value}
                    variant={formData.shape === shape.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleInputChange('shape', shape.value)}
                  >
                    <span className="mr-1">{shape.icon}</span>
                    {shape.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Auto-generated from shape, or enter custom description"
              className="bg-slate-50"
            />
            <p className="text-xs text-muted-foreground">
              Description is auto-generated from table shape but can be customized
            </p>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="flex items-center justify-center p-4 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 rounded-lg">
              <div
                className="flex items-center justify-center text-white text-xs font-medium border-2 border-white shadow-lg"
                style={{
                  backgroundColor: formData.color,
                  width: formData.shape === 'rectangle' ? '80px' : '60px',
                  height: '60px',
                  borderRadius: formData.shape === 'circle' ? '50%' : (formData.shape === 'rectangle' ? '8px' : '12px')
                }}
              >
                <div className="text-center">
                  <div>{formData.name || 'Table'}</div>
                  <div className="text-xs opacity-75">{formData.seats} seats</div>
                  <div className="text-xs opacity-60">Floor {formData.floor}</div>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {formData.description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t flex-shrink-0 sticky bottom-0 bg-white px-6 pb-4">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {table ? 'Update' : 'Create'} Table
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
